import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  CssBaseline,
  Divider,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  TextField,
  Card,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { CalendarToday } from "@mui/icons-material";

import { arSD } from "@mui/x-data-grid/locales";
import {
  useGetIncomeStatementQuery,
  useLazyGetIncomeStatementQuery,
} from "../../../redux/Slices/Income-StatementApi";

import { useGetFinancialPeriodsQuery } from "../../../redux/Slices/financial-periodsApi";
import detectFiscalPeriod from "hooks/detectFiscalPeriod";
import { set } from "date-fns";
import { useGetFinancialAccountsQuery } from "../../../redux/Slices/financial-accountsApi";
import MDBox from "components/MDBox";
import RTLDataGrid from "components/styled-component/RTLDataGrid";
import { GridToolbar } from "@mui/x-data-grid/internals";

// Summary Card Component

// create Income Statement Report
const IncomeStatementReport = () => {
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [endDate, setEndDate] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  });
  const [periodId, setPeriodId] = useState(() => {
    return detectFiscalPeriod(new Date().toISOString().split("T")[0]);
  });
  console.log("🚀 ~ IncomeStatementReport ~ periodId:", periodId);
  const [accountId, setAccountId] = useState(null);
  const { data: accounts, isLoading: accountsLoading } = useGetFinancialAccountsQuery(1);
  // Construct filter query based on accountId, periodId, startDate, and endDate
  const filterQuery = new URLSearchParams({
    account_id: accountId?.toString(),
    fiscal_period_id: periodId?.toString(),
    start_date: startDate,
    end_date: endDate,
  }).toString();
  // Fetch income statement data using the constructed filter query
  const { data, isLoading, isError, error } = useGetIncomeStatementQuery(filterQuery);
  const [getIncomeStatement, { data: incomeData, isLoading: incomeDataLoading }] =
    useLazyGetIncomeStatementQuery();
  const { data: periods, isLoading: periodsLoading } = useGetFinancialPeriodsQuery();
  const [incomeDataPerPeriod, setIncomeDataPerPeriods] = useState();
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("ar-EG", {
      style: "currency",
      currency: "EGP",
      minimumFractionDigits: 2,
    }).format(amount);
  };
  const handleChange = async (e) => {
    const { value, name } = e.target;
    console.log("🚀 ~ handleChange ~ e:", e.target.value);
    setPeriodId(e.target.value);
    await getIncomeStatement(value);
  };
  // DataGrid Columns for Revenues
  const revenueColumns = [
    {
      field: "account_code",
      headerName: "كود الحساب",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "account_name",
      headerName: "اسم الحساب",
      flex: 2,
      minWidth: 200,
    },
    {
      field: "amount",
      headerName: "المبلغ",
      flex: 1,
      minWidth: 150,

      valueFormatter: (params) => formatCurrency(params),
    },
  ];

  useEffect(() => {
    if (data) {
      setIncomeDataPerPeriods(data);
    }
  }, [data]);

  useEffect(() => {
    if (incomeData) {
      setIncomeDataPerPeriods(incomeData);
    }
  }, [incomeData, setIncomeDataPerPeriods]);
  // DataGrid Columns for Expenses
  const expenseColumns = [...revenueColumns];

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box p={3}>
        <Alert severity="error">
          حدث خطأ أثناء تحميل البيانات: {error?.data?.message || "خطأ غير معروف"}
        </Alert>
      </Box>
    );
  }

  const reportData = incomeDataPerPeriod?.data;
  const meta = incomeDataPerPeriod?.meta;

  return (
    <>
    <MDBox dir={"rtl"} pt={6} pb={3} >
      {/* Header */}
      <Card>

      
        <MDBox
          mx={2}
            mt={-3}
            py={3}
            px={2}
            borderRadius="lg"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              background: "linear-gradient(90deg, #ca9937, #e0b15a)",
              boxShadow: 3,
            }}
        
          flexWrap="wrap"
          gap={2}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold" color="text.main" gutterBottom>
              قائمة الدخل
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <CalendarToday fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {meta?.fiscal_period}
              </Typography>
              <Chip
                label={`${reportData?.period?.start_date} - ${reportData?.period?.end_date}`}
                size="small"
                variant="outlined"
              />
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary">
            تم الإنشاء في: {meta?.generated_at}
          </Typography>
        </MDBox>
      

      <Box display="flex" gap={2} mx={2} my={3}>
        <FormControl sx={{ mb: 2, minWidth: 200 }} flex={1}>
          <InputLabel id="account"> اختر الحساب </InputLabel>
          <Select
            label="اختر الحساب "
            /* error={!!errors.type} */
            name="account"
            value={accountId}
            onChange={(e) => {
              setAccountId(e.target.value);
            }}
            sx={{ height: 43 }}
          >
            {accounts?.data?.map((account) => (
              <MenuItem key={account.id} value={account.id}>
                {account.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl flex={1} sx={{ mb: 2 }}>
          <InputLabel id="type"> اختر الفترة </InputLabel>
          <Select
            label="اختر الفترة "
            /* error={!!errors.type} */
            name="type"
            value={periodId}
            onChange={(e) => {
              setPeriodId(e.target.value);
            }}
            sx={{ height: 43 }}
          >
            {periods?.data?.map((period) => (
              <MenuItem key={period.id} value={period.id}>
                {period.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl flex={1} sx={{}}>
          <TextField
            id="start-date"
            type="date"
            value={startDate}
            onChange={(e) => {
              if (e.target.value > endDate) {
                setEndDate(e.target.value);
              }
              const fiscalPeriod = detectFiscalPeriod(e.target.value);
              setPeriodId(fiscalPeriod);
              setStartDate(e.target.value);
              setEndDate(() => {
                const newEndDate = new Date(e.target.value);
                newEndDate.setDate(newEndDate.getDate() + 1);
                return newEndDate.toISOString().split("T")[0];
              });
            }}
            sx={{}}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </FormControl>
        <FormControl flex={1} sx={{}}>
          <TextField
            id="end-date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            sx={{}}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </FormControl>
      </Box>

      {/* Revenues DataGrid */}
      
        <Box sx={{ p: 2, backgroundColor: "success.light" }}>
          <Typography variant="h6" fontWeight="bold" color="success.dark">
            الإيرادات
          </Typography>
        </Box>
        <Divider />
        
          <RTLDataGrid
            rows={reportData?.revenues?.breakdown || []}
            columns={revenueColumns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[5, 10, 25, 50]}
            getRowId={(row) => row.account_code}
            disableRowSelectionOnClick
            isActions={false}
             slots={{
            toolbar: GridToolbar,
            footer: () => (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  p: 2,
                  
                  color: "success.dark",
                  fontWeight: 700,
                  fontSize: "1.1rem",
                }}
              >
                <Typography variant="h6" fontWeight="bold">
            إجمالي الإيرادات
          </Typography>
          <Typography variant="h6" fontWeight="bold" color="text.main">
            {formatCurrency(reportData?.revenues?.total || 0)}
          </Typography>
              </Box>
            ),
          }}
          />
        
    

      {/* Expenses DataGrid */}
      
        <Box sx={{ p: 2, backgroundColor: "primary.light" }}>
          <Typography variant="h6" fontWeight="bold" color="text.main">
            المصروفات
          </Typography>
        </Box>
        <Divider />
        <CssBaseline />
        
          <RTLDataGrid
            rows={reportData?.expenses?.breakdown || []}
            columns={expenseColumns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[5, 10, 25, 50]}
            getRowId={(row) => row.account_code}
            disableRowSelectionOnClick
            localeText={arSD.components.MuiDataGrid.defaultProps.localeText}
            isActions={false}
             slots={{
            toolbar: GridToolbar,
            footer: () => (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  p: 2,
                  backgroundColor: "primary.light",
                  color: "text.main",
                  fontWeight: 700,
                  fontSize: "1.1rem",
                }}
              >
              <Typography variant="h6" fontWeight="bold">
            إجمالي المصروفات
          </Typography>
          <Typography variant="h6" fontWeight="bold" color="error.main">
            {formatCurrency(reportData?.expenses?.total || 0)}
          </Typography>
              </Box>
            ),
          }}
          />
       
      

   
      
      </Card>
    </MDBox>
       
            <Divider sx={{ my: 1 }} />
         
<Grid container spacing={2}>
          <Grid item size={{xs: 12, sm: 3}}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body1" fontWeight="bold" color="text.main">
                إجمالي الإيرادات:
              </Typography>
              <Typography variant="body1" fontWeight="bold" color="success.dark">
                {formatCurrency(reportData?.summary?.total_revenue || 0)}
              </Typography>
            </Box>
          </Grid>
          <Grid item size={{xs: 12, sm: 3}}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body1" fontWeight="bold" color="text.main">
                إجمالي المصروفات:
              </Typography>
              <Typography variant="body1" fontWeight="bold" color="error.dark">
                {formatCurrency(reportData?.summary?.total_expenses || 0)}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item size={{xs: 12, sm: 3}}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h5" fontWeight="bold">
                صافي الدخل:
              </Typography>
              <Typography
                variant="h5"
                fontWeight="bold"
                color={reportData?.summary?.net_income >= 0 ? "success.main" : "error.main"}
              >
                {formatCurrency(reportData?.summary?.net_income || 0)}
              </Typography>
            </Box>
          </Grid>
        </Grid>

    </>
  );
};

export default IncomeStatementReport;
